import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { LaunchPricingNotice } from '../components/LaunchPricingNotice';
import {
  Store,
  Package,
  MessageSquare,
  Send,
  Users,
  TrendingUp,
  Shield,
  CreditCard,
  Camera,
  FileText,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Clock,
  Euro,
  Star,
  Heart,
  Zap
} from 'lucide-react';

export default function HowToSellPage() {
  const sellingMethods = [
    {
      id: 'catalog',
      icon: Store,
      title: 'Crea il tuo Negozio',
      subtitle: 'Vendi prodotti pronti o personalizzabili',
      color: 'bg-[#008080]',
      lightColor: 'bg-[#008080]/10',
      textColor: 'text-[#008080]',
      description: 'Costruisci la tua vetrina digitale e mostra le tue creazioni al mondo. Puoi caricare prodotti già pronti o offrire opzioni di personalizzazione.',
      steps: [
        'Crea il tuo profilo artigiano con foto e descrizione',
        'Aggiungi i tuoi prodotti con foto di qualità e descrizioni dettagliate',
        'Imposta prezzi, varianti e opzioni di personalizzazione',
        'Configura le regole di spedizione e i tempi di lavorazione',
        'Ricevi ordini e inizia a vendere!'
      ],
      benefits: [
        'Vetrina sempre disponibile 24/7',
        'Prodotti illimitati',
        'Gestione ordini integrata',
        'Analytics e statistiche'
      ],
      cta: 'Crea la tua Vetrina',
      ctaLink: '/plans'
    },
    {
      id: 'requests',
      icon: MessageSquare,
      title: 'Rispondi alle Richieste',
      subtitle: 'Trova clienti che cercano prodotti come i tuoi',
      color: 'bg-[#DAA520]',
      lightColor: 'bg-[#DAA520]/10',
      textColor: 'text-[#DAA520]',
      description: 'I clienti pubblicano richieste per prodotti personalizzati. Se la richiesta rientra nelle tue competenze, puoi inviare una proposta con il tuo preventivo.',
      steps: [
        'Imposta le categorie di prodotti che realizzi',
        'Ricevi notifiche quando arrivano richieste compatibili',
        'Leggi i dettagli e le esigenze del cliente',
        'Invia una proposta con prezzo, tempi e descrizione',
        'Se il cliente accetta, inizia la lavorazione!'
      ],
      benefits: [
        'Clienti già interessati al tuo tipo di prodotto',
        'Nessun costo per inviare proposte',
        'Visibilità alle tue specializzazioni',
        'Possibilità di acquisire clienti ricorrenti'
      ],
      cta: 'Esplora le Richieste',
      ctaLink: '/dashboard'
    },
    {
      id: 'direct',
      icon: Send,
      title: 'Ricevi Richieste Dirette',
      subtitle: 'I clienti ti contattano direttamente',
      color: 'bg-[#483D8B]',
      lightColor: 'bg-[#483D8B]/10',
      textColor: 'text-[#483D8B]',
      description: 'Quando un cliente visita il tuo profilo e vuole un prodotto su misura, può inviarti una richiesta diretta. Tu decidi se accettare e a quale prezzo.',
      steps: [
        'Un cliente visita il tuo profilo e le tue creazioni',
        'Ti invia una richiesta diretta con le sue esigenze',
        'Ricevi una notifica e leggi la richiesta',
        'Rispondi con una proposta personalizzata',
        'Il cliente accetta e procede al pagamento'
      ],
      benefits: [
        'Relazione diretta con il cliente',
        'Pieno controllo su prezzi e tempi',
        'Nessuna competizione con altri artigiani',
        'Ideale per lavori complessi e su misura'
      ],
      cta: 'Migliora il tuo Profilo',
      ctaLink: '/dashboard'
    }
  ];

  const features = [
    {
      icon: Shield,
      title: 'Pagamenti Sicuri',
      description: 'Stripe gestisce tutti i pagamenti. Ricevi il compenso quando il cliente conferma la ricezione.'
    },
    {
      icon: CreditCard,
      title: 'Commissioni Trasparenti',
      description: 'Solo l\'8% sul venduto, tutto incluso. Nessun costo fisso mensile per il piano Free.'
    },
    {
      icon: TrendingUp,
      title: 'Strumenti di Crescita',
      description: 'Analytics, gestione ordini, messaggistica integrata e strumenti per far crescere il tuo business.'
    },
    {
      icon: Users,
      title: 'Community Italiana',
      description: 'Entra in una community di artigiani italiani e raggiungi clienti che cercano il Made in Italy.'
    }
  ];

  const tips = [
    {
      icon: Camera,
      title: 'Foto di Qualità',
      description: 'Usa luce naturale, sfondo neutro e mostra i dettagli. Le foto sono il primo contatto con il cliente.'
    },
    {
      icon: FileText,
      title: 'Descrizioni Complete',
      description: 'Spiega materiali, dimensioni, tempi di realizzazione e possibilità di personalizzazione.'
    },
    {
      icon: Clock,
      title: 'Rispondi Velocemente',
      description: 'I clienti apprezzano risposte rapide. Cerca di rispondere entro 24 ore alle richieste.'
    },
    {
      icon: Star,
      title: 'Cura le Recensioni',
      description: 'Offri un servizio eccellente per ottenere recensioni positive che attraggono nuovi clienti.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Come Vendere su Rarifex | Guida per Artigiani Italiani</title>
        <meta name="description" content="Scopri come vendere le tue creazioni artigianali su Rarifex. Crea il tuo negozio, rispondi alle richieste dei clienti e fai crescere il tuo business Made in Italy." />
        <meta name="keywords" content="vendere artigianato online, marketplace artigiani italiani, vendere handmade, come vendere su Rarifex, artigianato Made in Italy" />
        <link rel="canonical" href="https://rarifex.com/come-vendere" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Come Vendere su Rarifex | Guida per Artigiani" />
        <meta property="og:description" content="Scopri i 3 modi per vendere le tue creazioni artigianali su Rarifex e raggiungere clienti in tutta Italia." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rarifex.com/come-vendere" />
        
        {/* Schema.org */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "Come Vendere su Rarifex",
            "description": "Guida completa per artigiani italiani che vogliono vendere le loro creazioni su Rarifex",
            "step": [
              {
                "@type": "HowToStep",
                "name": "Registrati come Artigiano",
                "text": "Crea il tuo account e scegli se registrarti come Hobbista o Artigiano con P.IVA"
              },
              {
                "@type": "HowToStep",
                "name": "Configura la tua Vetrina",
                "text": "Aggiungi foto, descrizione e informazioni sul tuo laboratorio"
              },
              {
                "@type": "HowToStep",
                "name": "Carica i tuoi Prodotti",
                "text": "Aggiungi i tuoi prodotti con foto di qualità e descrizioni dettagliate"
              },
              {
                "@type": "HowToStep",
                "name": "Inizia a Vendere",
                "text": "Ricevi ordini diretti, rispondi alle richieste dei clienti e fai crescere il tuo business"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#008080] via-[#006666] to-[#483D8B] text-white py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <Badge className="mb-4 bg-white/20 text-white border-white/30 hover:bg-white/30">
                <Sparkles className="w-3 h-3 mr-1" />
                Per gli Artigiani
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Come Vendere su Rarifex
              </h1>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Tre modi per trasformare la tua passione in un business: 
                crea il tuo negozio, rispondi alle richieste o ricevi ordini diretti.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-white text-[#008080] hover:bg-slate-100"
                  asChild
                >
                  <Link to="/plans">
                    Inizia Gratis
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10"
                  asChild
                >
                  <Link to="/crafters">
                    Scopri gli Artigiani
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* 3 Methods Section */}
        <section className="py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                3 Modi per Vendere
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Scegli il metodo più adatto al tuo stile di lavoro, oppure combinali tutti per massimizzare le opportunità.
              </p>
            </div>

            <div className="space-y-8">
              {sellingMethods.map((method, index) => (
                <Card key={method.id} className="overflow-hidden border-0 shadow-lg">
                  <div className="grid md:grid-cols-2 gap-0">
                    {/* Left: Info */}
                    <div className={`p-8 ${index % 2 === 1 ? 'md:order-2' : ''}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-12 h-12 ${method.color} rounded-xl flex items-center justify-center`}>
                          <method.icon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-slate-900">{method.title}</h3>
                          <p className="text-sm text-slate-500">{method.subtitle}</p>
                        </div>
                      </div>
                      
                      <p className="text-slate-600 mb-6">{method.description}</p>
                      
                      <div className="mb-6">
                        <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                          <Zap className={`w-4 h-4 ${method.textColor}`} />
                          Vantaggi
                        </h4>
                        <ul className="grid grid-cols-2 gap-2">
                          {method.benefits.map((benefit, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                              <CheckCircle className={`w-4 h-4 ${method.textColor} flex-shrink-0`} />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <Button className={`${method.color} hover:opacity-90`} asChild>
                        <Link to={method.ctaLink}>
                          {method.cta}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Link>
                      </Button>
                    </div>

                    {/* Right: Steps */}
                    <div className={`${method.lightColor} p-8 ${index % 2 === 1 ? 'md:order-1' : ''}`}>
                      <h4 className="font-semibold text-slate-800 mb-4">Come funziona</h4>
                      <ol className="space-y-4">
                        {method.steps.map((step, i) => (
                          <li key={i} className="flex gap-3">
                            <span className={`w-6 h-6 ${method.color} text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0`}>
                              {i + 1}
                            </span>
                            <span className="text-slate-700 text-sm pt-0.5">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Perché Scegliere Rarifex
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Tutto quello che ti serve per vendere online, senza complicazioni.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <Card key={index} className="p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-[#008080]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-7 h-7 text-[#008080]" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-600">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Consigli per Vendere di Più
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Piccoli accorgimenti che fanno la differenza nel conquistare i clienti.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {tips.map((tip, index) => (
                <div key={index} className="flex gap-4">
                  <div className="w-10 h-10 bg-[#DAA520]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <tip.icon className="w-5 h-5 text-[#DAA520]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{tip.title}</h3>
                    <p className="text-sm text-slate-600">{tip.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Preview */}
        <section className="py-16 bg-gradient-to-br from-[#008080]/5 to-[#483D8B]/5">
          <div className="max-w-4xl mx-auto px-4">
            <LaunchPricingNotice className="mb-8" />
            <Card className="p-8 md:p-12 text-center">
              <Euro className="w-12 h-12 text-[#008080] mx-auto mb-4" />
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                Commissioni Semplici e Trasparenti
              </h2>
              <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
                Nessun costo fisso per iniziare. Paghi solo quando vendi.
              </p>
              
              <div className="flex flex-wrap justify-center gap-8 mb-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#008080]">8%</div>
                  <div className="text-sm text-slate-500">commissione tutto incluso</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#008080]">€0</div>
                  <div className="text-sm text-slate-500">costi nascosti</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#008080]">€0</div>
                  <div className="text-sm text-slate-500">costi fissi mensili</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 justify-center">
                <Button size="lg" className="bg-[#008080] hover:bg-[#006666]" asChild>
                  <Link to="/plans">
                    Scopri i Piani
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/faq">
                    Leggi le FAQ
                  </Link>
                </Button>
              </div>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-[#008080] to-[#483D8B] text-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Heart className="w-12 h-12 mx-auto mb-4 opacity-80" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Pronto a Iniziare?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Unisciti agli artigiani italiani che stanno scegliendo Rarifex 
              per vendere le loro creazioni uniche.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-[#008080] hover:bg-slate-100"
                asChild
              >
                <Link to="/plans">
                  Diventa Artigiano Gratis
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10"
                asChild
              >
                <Link to="/contatti">
                  Hai Domande? Contattaci
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
